import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../base/LanguageSwitcher';

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isProductsOpen, setIsProductsOpen] = useState(false);
  const { t } = useTranslation();
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsProductsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isHomePage = location.pathname === '/';

  const categories = [
    { slug: 'vrijstaande-haarden', key: 'wallFireplaces', icon: 'ri-home-smile-line' },
    { slug: 'inbouwhaarden', key: 'builtIn', icon: 'ri-inbox-line' },
    { slug: '3-zijdige-haarden', key: 'threeSided', icon: 'ri-layout-3-line' }
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || !isHomePage ? 'bg-white shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="w-full px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 cursor-pointer">
            <div className="w-10 h-10 flex items-center justify-center">
              <i
                className={`ri-fire-line text-4xl ${
                  isScrolled || !isHomePage ? 'text-amber-600' : 'text-white'
                }`}
                style={{ width: '36px', height: '36px' }}
              ></i>
            </div>
            <span
              className={`text-2xl font-bold ${
                isScrolled || !isHomePage ? 'text-gray-900' : 'text-white'
              }`}
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Dutch Fires
            </span>
          </Link>

          {/* Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                isScrolled || !isHomePage
                  ? 'text-gray-700 hover:text-amber-600'
                  : 'text-white hover:text-amber-400'
              }`}
            >
              {t('common:nav.home')}
            </Link>

            {/* Products Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsProductsOpen(!isProductsOpen)}
                className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1 ${
                  isScrolled || !isHomePage
                    ? 'text-gray-700 hover:text-amber-600'
                    : 'text-white hover:text-amber-400'
                }`}
              >
                {t('common:nav.products')}
                <i className={`ri-arrow-down-s-line text-lg transition-transform ${isProductsOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {isProductsOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-2xl shadow-xl border border-gray-100 py-2 animate-fadeIn">
                  {categories.map((category) => (
                    <Link
                      key={category.slug}
                      to={`/category/${category.slug}`}
                      onClick={() => setIsProductsOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className={`${category.icon} text-xl text-amber-600`}></i>
                      </div>
                      <span className="text-sm text-gray-700 font-medium">{t(`common:nav.categories.${category.key}`)}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/inspiration"
              className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                isScrolled || !isHomePage
                  ? 'text-gray-700 hover:text-amber-600'
                  : 'text-white hover:text-amber-400'
              }`}
            >
              {t('common:nav.inspiration')}
            </Link>

            <Link
              to="/about"
              className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                isScrolled || !isHomePage
                  ? 'text-gray-700 hover:text-amber-600'
                  : 'text-white hover:text-amber-400'
              }`}
            >
              {t('common:nav.about')}
            </Link>

            <Link
              to="/dealer-registration"
              className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                isScrolled || !isHomePage
                  ? 'text-gray-700 hover:text-amber-600'
                  : 'text-white hover:text-amber-400'
              }`}
            >
              {t('common:nav.becomeDealer')}
            </Link>

            <LanguageSwitcher isScrolled={isScrolled || !isHomePage} />
          </nav>

          {/* CTA Button */}
          <Link
            to="/dealer-registration"
            className="hidden lg:inline-block px-6 py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-black font-semibold rounded-full hover:from-amber-600 hover:to-amber-700 transition-all shadow-lg cursor-pointer whitespace-nowrap"
          >
            {t('common:nav.becomeDealer')}
          </Link>
        </div>
      </div>
    </header>
  );
}